import { useState } from "react";
import { ThumbsDown, ThumbsUp } from "lucide-react";

/**
 * O polegar embaixo de uma resposta do Lucas.
 *
 * O único sinal que existia era "Isso resolveu", que é um encerramento: quem
 * clica está satisfeito, e quem não está fecha a aba sem deixar rastro. Aqui a
 * discordância também vira dado.
 *
 * O voto sobe NO CLIQUE, e o motivo vem depois. Segurar a avaliação até alguém
 * escrever perderia justamente o sinal de quem só queria marcar e sair — que é
 * a maioria. O motivo é o que corrige, mas o voto é o que mede.
 *
 * Componente sem rede por dentro: quem chama decide o que fazer com o voto. É o
 * que permite testá-lo sem `import.meta.env`, que não existe sob o Jest.
 */
export default function AnswerFeedback({ value, onRate, readOnly = false }) {
  const [showComment, setShowComment] = useState(false);
  const [comment, setComment] = useState(value?.comment || "");

  const helpful = value?.helpful ?? null;

  if (readOnly) {
    // Conversa encerrada é leitura. Sem voto não há o que mostrar — uma linha
    // dizendo "não avaliada" só ocuparia espaço em toda resposta antiga.
    if (helpful === null) return null;

    return (
      <div className="flex items-center gap-1.5 text-[11.5px] text-[#5c5f70] dark:text-white/50">
        {helpful ? (
          <ThumbsUp className="h-3.5 w-3.5" aria-hidden="true" />
        ) : (
          <ThumbsDown className="h-3.5 w-3.5" aria-hidden="true" />
        )}
        <span>{helpful ? "Você marcou que ajudou" : "Você marcou que não ajudou"}</span>
        {value?.comment && <span className="truncate">— “{value.comment}”</span>}
      </div>
    );
  }

  const rate = (nextHelpful) => {
    onRate?.({ helpful: nextHelpful });
    // Só o negativo pede motivo: perguntar a quem gostou é atrito em cima de
    // alguém que já estava servido.
    setShowComment(!nextHelpful);
  };

  const sendComment = () => {
    const text = comment.trim();
    if (!text) {
      setShowComment(false);
      return;
    }
    onRate?.({ helpful: false, comment: text });
    setShowComment(false);
  };

  const buttonClass = (active) =>
    `inline-flex h-7 w-7 items-center justify-center rounded-lg border transition-colors ${
      active
        ? "border-black/20 bg-black/[.06] text-[#15161f] dark:border-white/25 dark:bg-white/[.12] dark:text-white"
        : "border-transparent text-[#5c5f70] hover:bg-black/[.05] dark:text-white/45 dark:hover:bg-white/[.08]"
    }`;

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-1">
        <button
          type="button"
          aria-label="Esta resposta ajudou"
          aria-pressed={helpful === true}
          onClick={() => rate(true)}
          className={buttonClass(helpful === true)}
        >
          <ThumbsUp className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
        <button
          type="button"
          aria-label="Esta resposta não ajudou"
          aria-pressed={helpful === false}
          onClick={() => rate(false)}
          className={buttonClass(helpful === false)}
        >
          <ThumbsDown className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
      </div>

      {showComment && (
        <div className="flex flex-col gap-1.5">
          <textarea
            value={comment}
            onChange={(event) => setComment(event.target.value)}
            rows={2}
            maxLength={500}
            placeholder="O que faltou nesta resposta? (opcional)"
            className="w-full max-w-[420px] resize-none rounded-[10px] border border-black/10 bg-white/70 px-3 py-2 text-[12.5px] text-[#15161f] outline-none placeholder:text-[#8a8d9c] focus:border-black/25 dark:border-white/[.16] dark:bg-white/[.06] dark:text-white dark:placeholder:text-white/35 dark:focus:border-white/30"
          />
          <div className="flex gap-2">
            <button
              type="button"
              onClick={sendComment}
              className="inline-flex h-7 items-center rounded-[14px] bg-[#15161f] px-3 text-[11.5px] font-bold text-white dark:bg-white dark:text-[#0a0a0f]"
            >
              Enviar
            </button>
            <button
              type="button"
              onClick={() => setShowComment(false)}
              className="inline-flex h-7 items-center rounded-[14px] px-3 text-[11.5px] font-semibold text-[#5c5f70] dark:text-white/55"
            >
              Agora não
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
